<!--
  A change block's complexity in its head (logic/complexity): four small
  bars filled to its band and the band's word, quiet beside the block's
  delta. Hovering or focusing it shows the detail: both figures, the
  reasons and the risks. A native title alone is not shown on keyboard
  focus, so the detail is drawn here, as the mark's description.
-->
<script lang="ts">
  import type { Complexity } from '../engine/types';
  import { complexityDetail } from '../logic/complexity';

  interface Props {
    complexity: Complexity;
    /** Names the detail element (unique on the page). */
    id: string;
  }
  let { complexity, id }: Props = $props();

  const LEVEL = { trivial: 1, light: 2, moderate: 3, heavy: 4 } as const;
  const detail = $derived(complexityDetail(complexity));
  const level = $derived(LEVEL[complexity.band]);
</script>

<!-- A button so the keyboard reaches its detail; pressing it does nothing more (the block's head ignores presses on buttons). -->
<button type="button" class="cx cx-{complexity.band}" aria-label="Complexity: {detail.label}" aria-describedby="{id}-detail">
  <span class="cx-bars" aria-hidden="true">{#each [1, 2, 3, 4] as n (n)}<i class:on={n <= level}></i>{/each}</span>
  <span class="cx-word" aria-hidden="true">{detail.label}</span>{#if complexity.flags.length}<span class="cx-flag" aria-hidden="true">!</span>{/if}
  <span class="cx-detail" id="{id}-detail" role="tooltip">{detail.text}</span>
</button>

<style>
  .cx { position: relative; display: inline-flex; align-items: center; gap: 4px; padding: 0 2px; border: 0; background: none; font: 10px ui-monospace, monospace; color: var(--muted); white-space: nowrap; cursor: help; border-radius: 4px; }
  .cx-bars { display: inline-flex; align-items: flex-end; gap: 1px; height: 9px; }
  .cx-bars i { display: block; width: 2px; background: var(--line); border-radius: 1px; }
  .cx-bars i:nth-child(1) { height: 3px; }
  .cx-bars i:nth-child(2) { height: 5px; }
  .cx-bars i:nth-child(3) { height: 7px; }
  .cx-bars i:nth-child(4) { height: 9px; }
  .cx-trivial .on { background: var(--muted); }
  .cx-light .on { background: var(--good); }
  .cx-moderate .on { background: var(--warn); }
  .cx-heavy .on { background: var(--bad); }
  .cx-moderate .cx-word { color: var(--warn); }
  .cx-heavy .cx-word { color: var(--bad); }
  .cx-flag { color: var(--bad); font-weight: 700; }
  .cx-detail { display: none; position: absolute; top: calc(100% + 6px); left: 0; z-index: 6; width: max-content; max-width: min(420px, 70vw); padding: 6px 9px; border-radius: 6px; background: var(--fg); color: var(--surface); font: 12px/1.45 -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; white-space: normal; text-align: left; box-shadow: var(--shadow); pointer-events: none; }
  .cx:hover .cx-detail, .cx:focus-visible .cx-detail { display: block; }
  @media (max-width: 760px) { .cx-word { display: none; } }
</style>
